import { getAuth } from 'firebase/auth';
import { _config } from '../Constants';
import { app } from './firebaseApp';
import { getUserMemory, getCurrentUserName } from '../utilities/firebase';
import * as Sentry from '@sentry/react';


import axios from 'axios';
const HTTP_planner = `${_config.url}planner`;
const HTTP_generateLearningGoal = `${_config.url}generate-learning-goal`;

// Firebase app is initialized in firebaseApp.js (shared singleton).
const auth = getAuth(app);

export const lessonPlanning = (plannerText, targetLanguage = 'French') => {
  const name = getCurrentUserName();
  const age = localStorage.getItem('userAge');
  const level = localStorage.getItem('userLevel');
  const memory = getUserMemory() || undefined;

  let lessonPlanning;
  lessonPlanning = fetchCurrentLessonPlanner(plannerText.trim(), name, level, age, memory, targetLanguage);
  if (lessonPlanning && plannerText.trim()) {
    console.log('🗺️ Lesson planner created')
    return true // Indicate success
  } else if (!plannerText.trim()) {
    console.warn('Objective text is empty, not sending planner.')
    return false // Indicate failure (empty text)
  } else {
    console.warn('Something wrong, cannot send planner.')
    return false // Error
  }
}

/** PLANNER MANAGEMENT */

/* private */
async function _getUserPlanner(objective, name, level, age, memory, targetLanguage = 'French') {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const idToken = await user.getIdToken();
    let url = HTTP_planner;
    const userId = user.uid;

    try {
      const response = await axios.post(url,
        {objective, name, userId, level, age, memory, targetLanguage},
        {
            headers: {
            Authorization: `Bearer ${idToken}`, // Add ID token to headers
            "Content-Type": "application/json"
        },
      });
      return response.data.planning;
    } catch (error) {
      console.error('Error in fetching lesson planning:', error.response?.data?.error || error.message)
      return null;
    }
  } catch (error) {
    console.error('Error fetching lesson planning:', error);
    if (error.message === 'Unauthorized') {
      return {
        statusCode: 401,
        body: JSON.stringify({ error: 'Unauthorized' }),
      };
    } else {
      console.error('Error handling lesson planning:', error);
      return {
        statusCode: 500,
        body: JSON.stringify({ error: 'Internal server error' }),
      };
    }
  }
}

async function fetchCurrentLessonPlanner(objective, name, level, age, memory, targetLanguage = 'French') {
  const user = auth.currentUser;
  if (user) {
    const lessonPlanning = await _getUserPlanner(objective, name, level, age, memory, targetLanguage);
    return lessonPlanning;
  } else {
    console.warn('No user is currently logged in.');
    return null;
  }
}

/* Get existing planner for current user */
export async function fetchExistingPlanner() {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user is currently logged in.');
    return null;
  }

  const userId = user.uid;
  console.log('🔍 Fetching planner for userId:', userId);

  try {
    const idToken = await user.getIdToken();
    
    const response = await axios.get(`${HTTP_planner}?userId=${userId}`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json"
      },
    });
    
    console.log('✅ Planner fetched successfully:', response.data.planning);
    return response.data.planning;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log('❌ No existing planner found for userId:', userId);
      return null;
    }
    console.error('❌ Error fetching existing planner for userId:', userId, error.response?.data?.error || error.message);
    return null;
  }
}

/**
 * Build the POST /planner body used to regenerate a learning plan at a corrected level,
 * reusing the user's existing objective. Mirrors the fields the planner endpoint expects
 * (work/memory/organization are forwarded so the regenerated plan keeps its domain vocabulary).
 */
export function buildReplanRequest({
  objective,
  level,
  name,
  age,
  language,
  targetLanguage,
  userId,
  memory,
  work_title,
  work_domain,
  organizationId,
  firestoreDocId,
}) {
  return {
    firestoreDocId,
    name,
    level: level ?? 3, // Default to 3 if user has no level set
    age: age || '20',
    language: language || 'English',
    userId,
    memory: memory || undefined,
    work_title: work_title || undefined,
    work_domain: work_domain || undefined,
    targetLanguage: targetLanguage || 'French',
    organizationId: organizationId || undefined,
    objective,
  };
}

/**
 * Stable signature of a learning plan (goal + first activity title). Used to detect when a
 * regenerated plan has actually replaced the previous one, so we never re-apply the old plan
 * that is still the "latest" during the ~20s regeneration window.
 */
export function plannerSignature(plan) {
  if (!plan) return '';
  return `${plan.goal || ''}|${plan['1']?.title || ''}`;
}

/**
 * Trigger planner creation asynchronously (fire-and-forget)
 * Used during onboarding to start planner generation in the background
 *
 * @param {Object} data - Planner creation data
 * @param {string} data.objective - User's learning objective
 * @param {string} data.name - User's name
 * @param {number} data.level - User's level
 * @param {string} data.age - User's age group
 * @param {string} data.language - User's native language
 * @param {string} data.targetLanguage - Language being learned
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function triggerPlannerCreation(data) {
  const user = auth.currentUser;
  if (!user) {
    console.warn('🗺️ Planner trigger skipped: No user authenticated');
    return null;
  }

  try {
    const idToken = await user.getIdToken();
    const userId = user.uid;

    console.log('🗺️ Triggering planner creation for objective:', data.objective?.substring(0, 50) + '...');

    const response = await axios.post(HTTP_planner, {
      objective: data.objective,
      name: data.name,
      level: data.level,
      age: data.age,
      language: data.language,
      targetLanguage: data.targetLanguage,
      userId: userId,
    }, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 120000, // 2 minutes — Cloud Function can take a while
    });

    // Return planning data if the backend included it in the response
    if (response.data?.planning) {
      console.log('🗺️ Planner creation returned planning data directly');
      return response.data.planning;
    }

    console.log('🗺️ Planner creation completed (async processing):', response.data?.status || 'unknown');
    return null;
  } catch (error) {
    console.error('🗺️ Planner creation failed:', error.message);
    Sentry.captureException(error, {
      tags: { feature: 'planner', action: 'triggerPlannerCreation' },
      extra: { objective: data.objective?.substring(0, 100), level: data.level },
    });
    return null;
  }
}

/**
 * Generate a concise learning goal from conversation context
 * Calls /generate-learning-goal endpoint which uses a lightweight AI model
 *
 * @param {Object} data
 * @param {string} data.conversationContext - Conversation summary
 * @param {string} data.targetLanguage - Language being learned
 * @param {string} [data.userLanguage] - User's native/UI language code (e.g. 'fr', 'es')
 * @param {string} data.name - User's name
 * @param {string} [data.work_title] - User's work title
 * @param {string} [data.work_domain] - User's work domain
 * @returns {Promise<string|null>} Crafted objective or null on error
 */
export async function generateLearningGoal(data) {
  const user = auth.currentUser;
  if (!user) {
    console.warn('🎯 Learning goal generation skipped: No user authenticated');
    return null;
  }

  try {
    const idToken = await user.getIdToken();

    const response = await axios.post(HTTP_generateLearningGoal, {
      conversationContext: data.conversationContext,
      targetLanguage: data.targetLanguage,
      userLanguage: data.userLanguage,
      name: data.name,
      work_title: data.work_title,
      work_domain: data.work_domain,
      motivation: data.motivation,
      level: data.level,
    }, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 15000,
    });

    if (response.data?.objective) {
      console.log('🎯 Learning goal generated:', response.data.objective);
      return response.data.objective;
    }

    return null;
  } catch (error) {
    console.error('🎯 Learning goal generation failed:', error.message);
    return null;
  }
}